import { type UIMessage } from 'ai';
import Cookies from 'js-cookie';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Link2,
  Loader2,
  MessageSquare,
  MessageSquarePlus,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useListAgentChatWorkspacesQuery } from '@/services/api/agentChatWorkspaceService';
import { type AgentChatWorkspace } from '@/services/types/agent-chat-workspace-types';

import { AgentChatV2Card, type AgentChatV2ThreadState } from './FinchChatCard';

interface AgentChatV2Thread {
  id: string;
  title: string;
  workspace_id: string | null;
}

interface FinchChatThreadSessionResponse {
  thread: {
    id: string;
    active_message_id: string | null;
  };
  messages: UIMessage[];
  has_active_run: boolean;
  active_run_id: string | null;
}

interface FinchChatRunRecord {
  id: string;
}

interface ExportOldThreadResponse {
  thread: AgentChatV2Thread;
  exported_message_count: number;
  skipped_message_count: number;
}

interface LegacyAgentChatThread {
  id: string;
  title: string;
  system?: boolean;
  created_at?: string;
}

interface AgentChatV2ThreadSessionState extends AgentChatV2ThreadState {
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
}

const DEFAULT_THREAD_TITLE = 'Agent Chat V2';

function buildThreadsUrl(matterId: string): string {
  return `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/finch-chat/vercel/threads/`;
}

function buildBaseUrl(matterId: string, threadId: string): string {
  return `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/finch-chat/vercel/threads/${threadId}`;
}

function buildExportFromOldUrl(matterId: string): string {
  return `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/finch-chat/threads/export-old-thread/`;
}

function buildLegacyThreadsUrl(matterId: string): string {
  return `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/agent-threads/`;
}

function emptySessionState(): AgentChatV2ThreadSessionState {
  return {
    messages: [],
    activeRunId: null,
    activeMessageId: null,
    isLoading: false,
    hasLoaded: false,
    error: null,
  };
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': Cookies.get('csrftoken') ?? '',
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }

  return (await response.json()) as T;
}

function parseSession(response: FinchChatThreadSessionResponse): AgentChatV2ThreadState {
  return {
    messages: response.messages,
    activeRunId: response.has_active_run ? response.active_run_id : null,
    activeMessageId: response.thread.active_message_id,
  };
}

interface AgentChatV2ThreadsPanelProps {
  matterId: string;
  workspaceId?: string;
  onToolCall?: (toolName: string, input: unknown, output?: unknown) => void;
  initialThreadId?: string | null;
  onDeeplinkConsumed?: () => void;
}

export const AgentChatV2ThreadsPanel: React.FC<AgentChatV2ThreadsPanelProps> = ({
  matterId,
  workspaceId,
  onToolCall,
  initialThreadId: initialThreadIdProp,
  onDeeplinkConsumed,
}) => {
  const [threads, setThreads] = useState<AgentChatV2Thread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const initialThreadAppliedRef = useRef(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isExportFormOpen, setIsExportFormOpen] = useState(false);
  const [isExportingFromOld, setIsExportingFromOld] = useState(false);
  const [isLoadingLegacyThreads, setIsLoadingLegacyThreads] = useState(false);
  const [legacyThreads, setLegacyThreads] = useState<LegacyAgentChatThread[]>([]);
  const [includeSystemLegacyThreads, setIncludeSystemLegacyThreads] = useState(false);
  const [selectedLegacyThreadId, setSelectedLegacyThreadId] = useState('');
  const [isUpdatingWorkspaceLink, setIsUpdatingWorkspaceLink] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionByThreadId, setSessionByThreadId] = useState<Record<string, AgentChatV2ThreadSessionState>>({});
  const { data: workspaces = [] } = useListAgentChatWorkspacesQuery({ matterId });

  const setSessionState = useCallback((threadId: string, next: Partial<AgentChatV2ThreadSessionState>) => {
    setSessionByThreadId((previous) => ({
      ...previous,
      [threadId]: {
        ...(previous[threadId] ?? emptySessionState()),
        ...next,
      },
    }));
  }, []);

  const fetchThreadSession = useCallback(
    async (threadId: string): Promise<AgentChatV2ThreadState> => {
      const response = await fetchJson<FinchChatThreadSessionResponse>(
        `${buildBaseUrl(matterId, threadId)}/session/`,
      );
      return parseSession(response);
    },
    [matterId],
  );

  const refreshThreadSession = useCallback(
    async (threadId: string): Promise<AgentChatV2ThreadState> => {
      setSessionState(threadId, { isLoading: true, error: null });
      try {
        const session = await fetchThreadSession(threadId);
        setSessionState(threadId, {
          ...session,
          isLoading: false,
          hasLoaded: true,
          error: null,
        });
        return session;
      } catch (sessionError) {
        const message =
          sessionError instanceof Error ? sessionError.message : 'Failed to load thread session';
        setSessionState(threadId, {
          isLoading: false,
          hasLoaded: true,
          error: message,
        });
        throw sessionError;
      }
    },
    [fetchThreadSession, setSessionState],
  );

  const loadThreads = useCallback(async () => {
    setIsLoadingThreads(true);
    setError(null);
    setStatusMessage(null);
    try {
      const fetchedThreads = await fetchJson<AgentChatV2Thread[]>(buildThreadsUrl(matterId));
      let nextThreads = fetchedThreads;
      if (nextThreads.length === 0) {
        const created = await fetchJson<AgentChatV2Thread>(buildThreadsUrl(matterId), {
          method: 'POST',
          body: JSON.stringify({
            title: DEFAULT_THREAD_TITLE,
            workspace_id: workspaceId ?? null,
          }),
        });
        nextThreads = [created];
      }

      setThreads(nextThreads);

      // Handle deeplink initial thread selection (outside state updater to avoid side effects)
      if (initialThreadIdProp && !initialThreadAppliedRef.current) {
        initialThreadAppliedRef.current = true;
        onDeeplinkConsumed?.();
        const targetThread = nextThreads.find((thread) => thread.id === initialThreadIdProp);
        if (targetThread) {
          setSelectedThreadId(targetThread.id);
        } else {
          setError('This conversation could not be found. It may have been deleted.');
          // Fallback to the first available thread
          setSelectedThreadId(nextThreads[0]?.id ?? null);
        }
      } else {
        setSelectedThreadId((previous) => {
          if (previous && nextThreads.some((thread) => thread.id === previous)) {
            return previous;
          }
          return nextThreads[0]?.id ?? null;
        });
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load threads');
    } finally {
      setIsLoadingThreads(false);
    }
  }, [matterId, workspaceId, initialThreadIdProp, onDeeplinkConsumed]);

  useEffect(() => {
    loadThreads().catch(() => {});
  }, [loadThreads]);

  useEffect(() => {
    if (!selectedThreadId) {
      return;
    }
    const currentState = sessionByThreadId[selectedThreadId];
    if (currentState && (currentState.hasLoaded || currentState.error || currentState.isLoading)) {
      return;
    }
    refreshThreadSession(selectedThreadId).catch(() => {});
  }, [refreshThreadSession, selectedThreadId, sessionByThreadId]);

  useEffect(() => {
    if (!isExportFormOpen) {
      return;
    }

    const loadLegacyThreads = async () => {
      setIsLoadingLegacyThreads(true);
      try {
        const fetchedLegacyThreads = await fetchJson<LegacyAgentChatThread[]>(
          buildLegacyThreadsUrl(matterId),
        );
        setLegacyThreads(fetchedLegacyThreads);
      } catch (legacyLoadError) {
        setError(
          legacyLoadError instanceof Error
            ? legacyLoadError.message
            : 'Failed to load old threads',
        );
      } finally {
        setIsLoadingLegacyThreads(false);
      }
    };

    loadLegacyThreads().catch(() => {});
  }, [isExportFormOpen, matterId]);

  const importableLegacyThreads = useMemo(
    () =>
      includeSystemLegacyThreads
        ? legacyThreads
        : legacyThreads.filter((thread) => thread.system !== true),
    [includeSystemLegacyThreads, legacyThreads],
  );

  useEffect(() => {
    if (!isExportFormOpen) {
      return;
    }
    setSelectedLegacyThreadId((previous) => {
      if (
        previous &&
        importableLegacyThreads.some((thread) => thread.id === previous)
      ) {
        return previous;
      }
      return importableLegacyThreads[0]?.id ?? '';
    });
  }, [importableLegacyThreads, isExportFormOpen]);

  const handleCreateThread = useCallback(async () => {
    setIsCreating(true);
    setError(null);
    setStatusMessage(null);
    try {
      const created = await fetchJson<AgentChatV2Thread>(buildThreadsUrl(matterId), {
        method: 'POST',
        body: JSON.stringify({
          title: DEFAULT_THREAD_TITLE,
          workspace_id: workspaceId ?? null,
        }),
      });
      setThreads((previous) => [...previous, created]);
      setSelectedThreadId(created.id);
      setSessionState(created.id, emptySessionState());
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Failed to create thread');
    } finally {
      setIsCreating(false);
    }
  }, [matterId, setSessionState, workspaceId]);

  const handleExportFromOld = useCallback(async () => {
    if (!selectedLegacyThreadId) {
      setError('Select an old thread to import.');
      return;
    }

    setIsExportingFromOld(true);
    setError(null);
    setStatusMessage(null);
    try {
      const payload: Record<string, string> = {
        agent_thread_id: selectedLegacyThreadId,
      };
      if (workspaceId) {
        payload.workspace_id = workspaceId;
      }

      const result = await fetchJson<ExportOldThreadResponse>(
        buildExportFromOldUrl(matterId),
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      setThreads((previous) => {
        const withoutExisting = previous.filter(
          (thread) => thread.id !== result.thread.id,
        );
        return [result.thread, ...withoutExisting];
      });
      setSelectedThreadId(result.thread.id);
      setSessionState(result.thread.id, emptySessionState());
      setIsExportFormOpen(false);
      setStatusMessage(
        `Exported ${result.exported_message_count} message${result.exported_message_count === 1 ? '' : 's'} from old thread${result.skipped_message_count > 0 ? ` (${result.skipped_message_count} skipped)` : ''}.`,
      );
      await refreshThreadSession(result.thread.id);
    } catch (exportError) {
      setError(
        exportError instanceof Error
          ? exportError.message
          : 'Failed to export old thread',
      );
    } finally {
      setIsExportingFromOld(false);
    }
  }, [
    matterId,
    refreshThreadSession,
    selectedLegacyThreadId,
    setSessionState,
    workspaceId,
  ]);

  const handleRenameThread = useCallback(
    async (threadId: string, newTitle: string) => {
      try {
        const updated = await fetchJson<AgentChatV2Thread>(
          `${buildBaseUrl(matterId, threadId)}/`,
          {
            method: 'PUT',
            body: JSON.stringify({ title: newTitle }),
          },
        );
        setThreads((previous) =>
          previous.map((thread) => (thread.id === threadId ? { ...thread, title: updated.title } : thread)),
        );
      } catch (renameError) {
        setError(renameError instanceof Error ? renameError.message : 'Failed to rename thread');
      }
    },
    [matterId],
  );

  const handleAutoRenameThread = useCallback(
    async (threadId: string) => {
      try {
        const result = await fetchJson<{ suggested_name: string }>(
          `${buildBaseUrl(matterId, threadId)}/suggest-name/`,
          { method: 'POST' },
        );

        if (result.suggested_name && result.suggested_name !== 'New Thread') {
          const thread = threads.find((t) => t.id === threadId);
          if (thread && thread.title === DEFAULT_THREAD_TITLE) {
            await handleRenameThread(threadId, result.suggested_name);
          }
        }
      } catch {
        // Silent fail — auto-naming is best-effort
      }
    },
    [matterId, threads, handleRenameThread],
  );

  const selectedSession = useMemo(() => {
    if (!selectedThreadId) {
      return emptySessionState();
    }
    return sessionByThreadId[selectedThreadId] ?? emptySessionState();
  }, [selectedThreadId, sessionByThreadId]);

  const handleRefreshSelectedThread = useCallback(async (): Promise<AgentChatV2ThreadState> => {
    if (!selectedThreadId) {
      return {
        messages: [],
        activeRunId: null,
        activeMessageId: null,
      };
    }
    return refreshThreadSession(selectedThreadId);
  }, [refreshThreadSession, selectedThreadId]);

  const handleCancelRun = useCallback(
    async (runId: string): Promise<AgentChatV2ThreadState> => {
      if (!selectedThreadId) {
        return {
          messages: [],
          activeRunId: null,
          activeMessageId: null,
        };
      }

      await fetchJson<FinchChatRunRecord>(
        `${buildBaseUrl(matterId, selectedThreadId)}/runs/${runId}/cancel/`,
        { method: 'POST' },
      );
      return refreshThreadSession(selectedThreadId);
    },
    [matterId, refreshThreadSession, selectedThreadId],
  );

  const handleForceReset = useCallback(async (): Promise<AgentChatV2ThreadState> => {
    if (!selectedThreadId) {
      return {
        messages: [],
        activeRunId: null,
        activeMessageId: null,
      };
    }

    await fetchJson<{ reset: boolean; cancelled_count: number }>(
      `${buildBaseUrl(matterId, selectedThreadId)}/runs/force-reset/`,
      { method: 'POST' },
    );
    return refreshThreadSession(selectedThreadId);
  }, [matterId, refreshThreadSession, selectedThreadId]);

  const workspaceNameById = useMemo(
    () =>
      (workspaces as AgentChatWorkspace[]).reduce<Record<string, string>>(
        (lookup, workspace) => {
          lookup[workspace.id] = workspace.name;
          return lookup;
        },
        {},
      ),
    [workspaces],
  );

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) ?? null,
    [threads, selectedThreadId],
  );
  const showWorkspaceLinking = Boolean(workspaceId);
  const selectedThreadTitle = selectedThread?.title;
  const selectedThreadWorkspaceId = selectedThread?.workspace_id ?? null;

  const handleSetWorkspaceLink = useCallback(
    async (targetWorkspaceId: string | null) => {
      if (!selectedThreadId) return;
      setIsUpdatingWorkspaceLink(true);
      setError(null);
      try {
        const updated = await fetchJson<AgentChatV2Thread>(
          `${buildBaseUrl(matterId, selectedThreadId)}/workspace-link/`,
          {
            method: 'POST',
            body: JSON.stringify({ workspace_id: targetWorkspaceId }),
          },
        );
        setThreads((previous) =>
          previous.map((thread) =>
            thread.id === selectedThreadId
              ? { ...thread, workspace_id: updated.workspace_id }
              : thread,
          ),
        );
      } catch (linkError) {
        setError(
          linkError instanceof Error
            ? linkError.message
            : 'Failed to update workspace link',
        );
      } finally {
        setIsUpdatingWorkspaceLink(false);
      }
    },
    [matterId, selectedThreadId],
  );

  const handleWorkspaceSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const nextValue = event.target.value;
      handleSetWorkspaceLink(nextValue === "__unlinked__" ? null : nextValue).catch(() => {});
    },
    [handleSetWorkspaceLink],
  );

  let legacyThreadOptions: React.ReactNode;
  if (isLoadingLegacyThreads) {
    legacyThreadOptions = <option value="">Loading old threads...</option>;
  } else if (importableLegacyThreads.length === 0) {
    legacyThreadOptions = <option value="">No old threads found</option>;
  } else {
    legacyThreadOptions = importableLegacyThreads.map((thread) => (
      <option key={thread.id} value={thread.id}>
        {thread.system ? `[Finch] ${thread.title}` : thread.title}
      </option>
    ));
  }

  const handleFirstMessage = useCallback(() => {
    if (selectedThreadId && selectedThreadTitle === DEFAULT_THREAD_TITLE) {
      handleAutoRenameThread(selectedThreadId).catch(() => {});
    }
  }, [selectedThreadId, selectedThreadTitle, handleAutoRenameThread]);

  const handleCopyThreadLink = useCallback(
    (threadId: string) => {
      const url = new URL(window.location.href);
      url.search = '';
      url.searchParams.set('finchgpt', threadId);
      url.searchParams.set('chat_version', 'agent_v2');
      navigator.clipboard.writeText(url.toString()).then(() => {
        setStatusMessage('Link copied to clipboard');
        setTimeout(() => setStatusMessage(null), 2000);
      }).catch(() => {
        setError('Failed to copy link');
      });
    },
    [],
  );

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div
        className={cn(
          'h-full flex-shrink-0 border-r border-zinc-200/60 bg-slate-50/30 transition-[width] duration-200 overflow-hidden',
          isSidebarOpen ? 'w-[200px]' : 'w-10',
        )}
      >
        {isSidebarOpen ? (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-3 pt-3 pb-2 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-semibold text-slate-700 tracking-tight">Threads</h2>
                {threads.length > 0 && (
                  <span className="text-[10px] font-medium px-1.5 py-px rounded-full bg-slate-200/70 text-slate-500">
                    {threads.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  disabled={isCreating}
                  onClick={handleCreateThread}
                  className={cn(
                    'w-6 h-6 rounded-lg flex items-center justify-center',
                    'bg-slate-700 text-white shadow-sm',
                    'hover:bg-slate-800 transition-colors',
                    isCreating && 'opacity-50 pointer-events-none',
                  )}
                  title="New thread"
                >
                  {isCreating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />}
                </button>
                <button
                  type="button"
                  disabled={isExportingFromOld}
                  onClick={() => {
                    setIsExportFormOpen((previous) => !previous);
                    setError(null);
                    setStatusMessage(null);
                  }}
                  className={cn(
                    'w-6 h-6 rounded-lg flex items-center justify-center',
                    'text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors',
                    isExportingFromOld && 'opacity-50 pointer-events-none',
                  )}
                  title="Export from old thread"
                >
                  {isExportingFromOld ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="mt-0.5 mx-3 h-px bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />

            {error && <p className="text-[11px] text-red-500 px-3 pt-1">{error}</p>}
            {statusMessage && (
              <p className="text-[11px] text-emerald-600 px-3 pt-1">{statusMessage}</p>
            )}

            {showWorkspaceLinking && selectedThreadId && (
              <div className="mx-3 mt-2 rounded-md border border-slate-200/80 bg-white/80 p-2">
                <p className="text-[10px] font-medium text-slate-500">Workspace Link</p>
                <select
                  value={selectedThreadWorkspaceId ?? "__unlinked__"}
                  onChange={handleWorkspaceSelectChange}
                  disabled={isUpdatingWorkspaceLink}
                  className={cn(
                    "mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                  )}
                >
                  <option value="__unlinked__">Unlinked</option>
                  {(workspaces as AgentChatWorkspace[]).map((workspace) => (
                    <option key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isExportFormOpen && (
              <div className="mx-3 mt-2 rounded-md border border-slate-200/80 bg-white/80 p-2 space-y-1.5">
                <p className="text-[10px] font-medium text-slate-500">
                  Select old thread
                </p>
                <label
                  htmlFor="include-system-legacy-threads"
                  className="flex items-center gap-1.5 text-[10px] text-slate-600"
                >
                  <input
                    id="include-system-legacy-threads"
                    type="checkbox"
                    checked={includeSystemLegacyThreads}
                    onChange={(event) =>
                      setIncludeSystemLegacyThreads(event.target.checked)
                    }
                    disabled={isLoadingLegacyThreads || isExportingFromOld}
                    className="h-3 w-3 rounded border-slate-300"
                  />
                  Include Finch/system threads
                </label>
                <select
                  value={selectedLegacyThreadId}
                  onChange={(event) =>
                    setSelectedLegacyThreadId(event.target.value)
                  }
                  disabled={isLoadingLegacyThreads || isExportingFromOld}
                  className={cn(
                    'w-full rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                  )}
                >
                  {legacyThreadOptions}
                </select>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={
                      isExportingFromOld ||
                      isLoadingLegacyThreads ||
                      !selectedLegacyThreadId
                    }
                    onClick={() => {
                      handleExportFromOld().catch(() => {});
                    }}
                    className={cn(
                      'rounded-md px-2 py-1 text-[10px] font-semibold transition-colors',
                      'bg-slate-700 text-white hover:bg-slate-800',
                      isExportingFromOld && 'opacity-50 pointer-events-none',
                    )}
                  >
                    {isExportingFromOld ? 'Exporting...' : 'Export'}
                  </button>
                  <button
                    type="button"
                    disabled={isExportingFromOld}
                    onClick={() => {
                      setIsExportFormOpen(false);
                    }}
                    className={cn(
                      'rounded-md px-2 py-1 text-[10px] font-semibold transition-colors',
                      'border border-slate-300 text-slate-600 hover:bg-slate-100',
                      isExportingFromOld && 'opacity-50 pointer-events-none',
                    )}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Thread list */}
            <div className="flex-1 overflow-y-auto px-2 pt-1 pb-2">
              {isLoadingThreads ? (
                <div className="space-y-1 p-0.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-8 rounded-lg bg-slate-100 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-0.5">
                  {threads.map((thread) => (
                    <ThreadSidebarItem
                      key={thread.id}
                      thread={thread}
                      showLinkIndicator={showWorkspaceLinking}
                      isLinked={Boolean(thread.workspace_id)}
                      linkedWorkspaceLabel={
                        thread.workspace_id
                          ? (workspaceNameById[thread.workspace_id] ??
                            thread.workspace_id)
                          : 'Unlinked'
                      }
                      isSelected={selectedThreadId === thread.id}
                      onSelect={setSelectedThreadId}
                      onRename={handleRenameThread}
                      onCopyLink={handleCopyThreadLink}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Collapsed rail */
          <div className="flex flex-col items-center pt-3 gap-2 h-full">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={isCreating}
              onClick={handleCreateThread}
              className={cn(
                'w-7 h-7 rounded-lg flex items-center justify-center',
                'text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors',
                isCreating && 'opacity-50 pointer-events-none',
              )}
              title="New thread"
            >
              {isCreating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
            </button>
            <button
              type="button"
              disabled={isExportingFromOld}
              onClick={() => {
                setIsSidebarOpen(true);
                setIsExportFormOpen(true);
                setError(null);
                setStatusMessage(null);
              }}
              className={cn(
                'w-7 h-7 rounded-lg flex items-center justify-center',
                'text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors',
                isExportingFromOld && 'opacity-50 pointer-events-none',
              )}
              title="Export from old thread"
            >
              {isExportingFromOld ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-4 w-4" />}
            </button>
            <div className="w-5 h-px bg-slate-200 my-0.5" />
            {threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => {
                  setSelectedThreadId(thread.id);
                  setIsSidebarOpen(true);
                }}
                className={cn(
                  'w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                  selectedThreadId === thread.id
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
                )}
                title={thread.title}
              >
                {showWorkspaceLinking ? (
                  <div className="relative">
                    <MessageSquare className="h-3.5 w-3.5" />
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-white",
                        thread.workspace_id ? "bg-emerald-400" : "bg-slate-300",
                      )}
                    />
                  </div>
                ) : (
                  <MessageSquare className="h-3.5 w-3.5" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chat card */}
      <div className="flex-1 min-w-0 min-h-0">
        {isLoadingThreads || !selectedThreadId ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
          </div>
        ) : (
          <AgentChatV2Card
            key={selectedThreadId}
            matterId={matterId}
            threadId={selectedThreadId}
            isInitializing={selectedSession.isLoading}
            initializationError={selectedSession.error}
            initialState={{
              messages: selectedSession.messages,
              activeRunId: selectedSession.activeRunId,
              activeMessageId: selectedSession.activeMessageId,
            }}
            onRefreshThreadState={handleRefreshSelectedThread}
            onCancelRun={handleCancelRun}
            onForceReset={handleForceReset}
            onToolCall={onToolCall}
            onFirstMessage={handleFirstMessage}
          />
        )}
      </div>
    </div>
  );
};

interface ThreadSidebarItemProps {
  thread: AgentChatV2Thread;
  showLinkIndicator: boolean;
  isLinked: boolean;
  linkedWorkspaceLabel: string;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, newTitle: string) => Promise<void>;
  onCopyLink?: (id: string) => void;
}

const ThreadSidebarItem: React.FC<ThreadSidebarItemProps> = ({
  thread,
  showLinkIndicator,
  isLinked,
  linkedWorkspaceLabel,
  isSelected,
  onSelect,
  onRename,
  onCopyLink,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(thread.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== thread.title) {
      onRename(thread.id, trimmed).catch(() => {});
    }
    setIsRenaming(false);
  }, [renameValue, thread.title, thread.id, onRename]);

  const cancelRename = useCallback(() => {
    setRenameValue(thread.title);
    setIsRenaming(false);
  }, [thread.title]);
  let linkIndicatorClass = "bg-slate-400";
  if (isLinked) {
    linkIndicatorClass = "bg-emerald-300";
  } else if (isSelected) {
    linkIndicatorClass = "bg-slate-300";
  }

  if (isRenaming) {
    return (
      <div className="flex items-center gap-0.5 px-1">
        <input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') cancelRename();
          }}
          onBlur={commitRename}
          className={cn(
            'flex-1 min-w-0 rounded-lg px-2 py-1.5 text-[11px] font-medium',
            'bg-white border border-zinc-300 outline-none focus:border-zinc-500',
          )}
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commitRename}
          className="shrink-0 p-0.5 rounded text-zinc-500 hover:text-zinc-700"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancelRename}
          className="shrink-0 p-0.5 rounded text-zinc-500 hover:text-zinc-700"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => onSelect(thread.id)}
        onDoubleClick={() => {
          setRenameValue(thread.title);
          setIsRenaming(true);
        }}
        className={cn(
          'w-full text-left rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-all duration-150',
          isSelected
            ? 'bg-slate-700 text-white shadow-sm'
            : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100',
        )}
        title={
          showLinkIndicator
            ? (isLinked ? `Linked to ${linkedWorkspaceLabel}` : "Unlinked")
            : thread.title
        }
      >
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate">{thread.title}</span>
          {showLinkIndicator && (
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                linkIndicatorClass,
              )}
            />
          )}
        </div>
      </button>
      {onCopyLink && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCopyLink(thread.id);
          }}
          className={cn(
            'absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity',
            'opacity-0 group-hover:opacity-100',
            isSelected
              ? 'text-white/70 hover:text-white hover:bg-white/10'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200',
          )}
          title="Copy link"
        >
          <Link2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

// Backward-compatible alias for existing imports.
export const FinchChatThreadsPanel = AgentChatV2ThreadsPanel;
